import React, {useContext, useReducer,} 	from 'react';
import LangContext 							from '../../LangContext';

const INIT = 'init';
const SET_ERROR = 'setError';
const SET_SUCCESS = 'setSuccess';
const CLEAR_ERROR = 'clearError';
const CLEAR_ALL_ERRORS = 'clearAllErrors';
const CLEAR_ERROR_SUCCESS = 'clearErrorSuccess';
const VALIDATE_FORM = 'validateForm';
const CLEAR_SUCCESS = 'clearSuccess';

/**
 * Returns Reducer object for useErrorForm hook
 *
 * @param {object} state
 * @param {object} action
 * @exception
 * @returns {object}
 */
const reducer = (state, action) => {
	switch (action.type) {
		case INIT:
			return {
				...state,
				...action.data,
			};
		case CLEAR_ERROR:
			return {
				...state,
				errors: action.data,
			};
		case SET_ERROR:
			return {
				...state,
				errors: {
					...state.errors,
					...action.data,
				},
			};
		case SET_SUCCESS:
			return {
				...state,
				success: {
					...state.success,
					...action.data,
				},
			};
		case CLEAR_ALL_ERRORS:
			return {
				...state,
				errors: {},
			};
		case CLEAR_ERROR_SUCCESS:
			return {
				...state,
				errors: {
					...state.errors,
					...action.data.errors,
				},
				success: {
					...state.success,
					...action.data.success,
				},
			};
		case CLEAR_SUCCESS:
			return {
				...state,
				success: action.data,
			};
		case VALIDATE_FORM:
			return {
				...state,
				errors: {
					...state.errors,
					...action.data.errors,
				},
				success: {
					...state.success,
					...action.data.success,
				},
			};
		default:
			throw new Error("Don't fount ");
	}
};

/**
 * Hook for validate data
 *
 * @param {object} rules, This is an object that accepts a key to which the error will be attached and a value in the
 * form of an array of validators that will be executed sequentially
 * @returns {object}
 */


const useErrorForm = (rules = {}) => {
	const context = useContext(LangContext);

	const [ state, dispatch, ] = useReducer(
		reducer, {
			errors: {},
			rules,
			success: {},
		});

	const clearError = (key) => {
		if (key) {
			return dispatch({
				data: Object.fromEntries(Object
					.entries({ ...state.errors, })
					.filter(([ value, ]) => {
						return Array.isArray(key) ? !key.includes(value) : key !== value;
					})),
				type: CLEAR_ERROR,
			});
		}

		return dispatch({ type: CLEAR_ALL_ERRORS, });
	};
	
	const clearSuccess = (key) => {
		return dispatch({
			data: Object.fromEntries(Object
				.entries({ ...state.success, })
				.filter(([ value, ]) => {
					return Array.isArray(key) ? !key.includes(value) : key !== value;
				})),
			type: CLEAR_SUCCESS,
		});
	};

	/**
	 * Checks for errors in an object
	 *
	 * @returns {boolean}
	 */
	const hasError = () => {
		return Object.values(state.errors).length !== 0;
	};

	/**
	 * Checks for errors in an object by key
	 *
	 * @returns {boolean}
	 */
	const isError = (key) => {
		return state.errors.hasOwnProperty(key);
	};

	/**
	 * Checks for success in an object
	 *
	 * @returns {boolean}
	 */
	const isSuccess = (key) => {
		return state.success.hasOwnProperty(key);
	};

	/**
	 * Set error by key with message
	 * @param {string} key
	 * @param {(string|string[])} value - can be a string or an array of strings
	 */
	const setError = (key, value) => {
		dispatch({
			data: {
				[key]: {
					errors: Array.isArray(value) ? value : [ value, ],
				},
			},
			type: SET_ERROR,
		});
	};

	/**
	 *
	 * @param {object} errors
	 */
	const setErrorModel = (errors = {}) => {
		for (const [key, value] of Object.entries(errors)) {
			setError(key, value);
			clearSuccess(key);
		}
	};

	/**
	 * Set error by key with message
	 * @param {string} key
	 * @param {(string|string[])} value - can be a string or an array of strings
	 */
	const getError = (key) => {
		if(!isError(key)) return null;
		return state.errors.hasOwnProperty(key) && state.errors[key];
	};

	/**
	 * Set success by key with message
	 * @param {string} key
	 * @param {(string|string[])} value - can be a string or an array of strings
	 */
	const setSuccess = (key, value) => {
		dispatch({
			data: {
				[key]: {
					success: Array.isArray(value) ? value : [ value, ],
				},
			},
			type: SET_SUCCESS,
		});
	};

	/**
	 * Validate data, mapKeyValue has key error and value
	 * @param {object} mapKeyValue
	 * @param {object} newRules
	 */
	const validateForm = (mapKeyValue = {}, newRules = {}) => {
		let success = {};
		const errors = {};
		let rules = {...state.rules, ...newRules};
		Object.entries(mapKeyValue).map(([ key, value, ]) => {
			if (rules.hasOwnProperty(key)) {
				for (const e of rules[key]) {
					const validate = e({value:value, callback: {translate: context.translate}});
					if (!validate.rules) {
						if (!success.hasOwnProperty(key)) {
							success[key] = {
								errors: [],
								success: [ validate.success, ],
							};
						}
					} else {
						let arrError = [validate.error];
						if (errors.hasOwnProperty(key)) {
							arrError.push(errors[key].errors);
						}
						errors[key] = {
							errors: arrError,
							success: [],
						};
					}
				}
			} else {
				errors[key] = {
					errors: [ `Don't found ${ key } rules`, ],
					success: [],
				};
			}
		});

		success = Object.fromEntries(Object.entries(success).filter(([ key, ]) => !errors.hasOwnProperty(key)));

		dispatch({ data: { errors, success, }, type: VALIDATE_FORM, });

		return Object.keys(errors).length === 0;
	};

	/**
	 * Validate data, mapKeyValue has key error and value
	 * @param {string} key
	 * @param {object} newRules
	 */
	const validateField = async (obj, key, newRules = {}) => {
		let success = {};
		const errors = {};
		let rules = {...state.rules, ...newRules};

		const value = obj[key];

		if (rules.hasOwnProperty(key)) {
			for (const e of rules[key]) {
				const validate = e({value:value, callback: {translate: context.translate}});
				if (!validate.rules) {
					if (!success.hasOwnProperty(key)) {
						success[key] = {
							errors: [],
							success: [ validate.success, ],
						};
					}
				} else {
					let arrError = [validate.error];
					if (errors.hasOwnProperty(key)) {
						arrError.push(errors[key].errors);
					}
					clearSuccess(key);
					errors[key] = {
						errors: arrError,
						success: [],
					};
				}
			}
		} else {
			errors[key] = {
				errors: [ `Don't found ${ key } rules`, ],
				success: [],
			};
		}

		success = Object.fromEntries(Object.entries(success).filter(([ key, ]) => !errors.hasOwnProperty(key)));

		dispatch({ data: { errors, success, }, type: VALIDATE_FORM, });

		return Object.keys(errors).length === 0;
	};

	/**
	 *
	 * @param {string} key
	 */
	const clearErrorSuccess = (key) => {
		let success = {};
		let errors = {};
		errors = Object.fromEntries(Object
			.entries({ ...state.errors})
			.filter(([ value, ]) => {
				return Array.isArray(key) ? !key.includes(value) : key !== value;
			}));
		success = Object.fromEntries(Object
			.entries({ ...state.success})
			.filter(([ value, ]) => {
				return Array.isArray(key) ? !key.includes(value) : key !== value;
			}));
		return dispatch({
			data: { errors, success, },
			type: CLEAR_ERROR_SUCCESS,
		});
	};

	const checkErrorClass = (key, type) => {
		if (isError(key)) {
			switch (type) {
				case 'input':		return 'input-field--error';
				case 'select':		return 'select-field--error';
				case 'text-area':	return 'textarea-field--error';
				default:			return 'input-field--error';
			}
		}
		return '';
	};

	const getErrors = (key) => {
		if (!isError(key)) return '';
		if(Array.isArray(getError(key).errors)) {
			return getError(key).errors?.map?.((item, i) => (
				<div key={ i } className="error-text paddings--sides_20 margin--bottom_10 margin--top_5">{ item }</div>)
			);
		} else {
			return (<div className="error-text paddings--sides_20 margin--bottom_10 margin--top_5">{ getError(key) }</div>)
		}
	};
	
	const handleServerErrors = (severError, errorMap) => {
		if(!errorMap.hasOwnProperty(severError)) {
			setError('unknownError', severError)
		} else {
			const key = errorMap[severError].key;
			const error = errorMap[severError].message
			setError(key, error)
		}
	};
	
	return {
		clearError,
		errors: state.errors,
		hasError,
		isError,
		isSuccess,
		setError,
		setSuccess,
		validateForm,
		clearErrorSuccess,
		getError,
		validateField,
		setErrorModel,
		clearSuccess,
		checkErrorClass,
		getErrors,
		handleServerErrors
	};
};

export default useErrorForm;